import React, { useState, useMemo, useCallback, useRef, useEffect } from 'react';
import {
  IonPage, IonHeader, IonToolbar, IonTitle, IonButtons, IonMenuButton,
  IonContent, IonIcon, IonSpinner,
} from '@ionic/react';
import {
  people, peopleCircle, gitNetwork, statsChart,
  arrowUp, arrowDown, eye, home, shieldCheckmark,
  checkmarkCircle, closeCircle, chevronDown, chevronUp,
  documentTextOutline, swapHorizontalOutline, skullOutline,
  walkOutline, addCircleOutline, removeCircleOutline,
  downloadOutline,
} from 'ionicons/icons';
import { useIonRouter } from '@ionic/react';
import { ResponsiveLine } from '@nivo/line';
import { ResponsiveBar } from '@nivo/bar';
import { ResponsivePie } from '@nivo/pie';
import { nivoTheme } from '@/utils/nivoTheme';
import { useDashboardStats, useReportTrends, useGdprStats, useRegistryStats, useMemberBreakdowns } from '@/hooks/useDashboard';
import type { RegistryMember, AgeRangeBucket } from '@/api/dashboard';
import { exportGdprList } from '@/api/dashboard';
import { downloadBlob } from '@/utils/download';
import { CIVIL_STATUS_LABELS_ALL } from '@/utils/labels';
import {
  CHURCH_STATUS_LABELS, MEMBERSHIP_TYPE_LABELS,
} from '@/utils/labels';

/* ── Age range defaults + localStorage persistence ── */
const DEFAULT_MEMBER_RANGES: AgeRangeBucket[] = [
  { min: 16, max: 20 }, { min: 21, max: 30 }, { min: 31, max: 40 },
  { min: 41, max: 50 }, { min: 51, max: 60 }, { min: 61, max: 70 }, { min: 71, max: 200 },
];
const DEFAULT_DEPENDENT_RANGES: AgeRangeBucket[] = [
  { min: 0, max: 1 }, { min: 2, max: 3 }, { min: 4, max: 6 },
  { min: 7, max: 10 }, { min: 11, max: 14 }, { min: 15, max: 18 }, { min: 19, max: 200 },
];

const LS_KEY_CUSTOM_M = 'betel_age_custom_members';
const LS_KEY_CUSTOM_D = 'betel_age_custom_dependents';

function loadCustomRanges(key: string, fallback: AgeRangeBucket[]): AgeRangeBucket[] {
  try {
    const v = localStorage.getItem(key);
    if (v) { const parsed = JSON.parse(v); if (Array.isArray(parsed) && parsed.length > 0) return parsed; }
  } catch { /* ignore */ }
  return fallback;
}

/* ── Inline editor for custom age ranges ── */
const AgeRangeEditor: React.FC<{
  ranges: AgeRangeBucket[];
  onChange: (ranges: AgeRangeBucket[]) => void;
  label: string;
}> = ({ ranges, onChange, label }) => {
  const update = (idx: number, field: 'min' | 'max', val: number) => {
    const next = ranges.map((r, i) => i === idx ? { ...r, [field]: val } : r);
    onChange(next);
  };
  const add = () => {
    const last = ranges[ranges.length - 1];
    const newMin = last ? last.max + 1 : 0;
    onChange([...ranges, { min: newMin, max: newMin + 9 }]);
  };
  const remove = (idx: number) => {
    if (ranges.length <= 1) return;
    onChange(ranges.filter((_, i) => i !== idx));
  };

  return (
    <div className="age-editor">
      <span className="age-editor__label">{label}</span>
      <div className="age-editor__chips">
        {ranges.map((r, i) => (
          <div key={i} className="age-editor__chip">
            <input
              type="number" min={0} max={199} value={r.min}
              onChange={(e) => update(i, 'min', parseInt(e.target.value) || 0)}
              className="age-editor__input"
            />
            <span className="age-editor__sep">–</span>
            <input
              type="number" min={r.min} max={200} value={r.max}
              onChange={(e) => update(i, 'max', parseInt(e.target.value) || r.min)}
              className="age-editor__input"
            />
            {ranges.length > 1 && (
              <button className="age-editor__remove" onClick={() => remove(i)} title="Șterge">
                <IonIcon icon={removeCircleOutline} />
              </button>
            )}
          </div>
        ))}
        <button className="age-editor__add" onClick={add}>
          <IonIcon icon={addCircleOutline} /> Adaugă
        </button>
      </div>
    </div>
  );
};

/* ── Helpers ── */
const pct = (n: number, total: number) => total ? Math.round((n / total) * 100) : 0;

const StatCard: React.FC<{
  label: string;
  value: number | string;
  icon: string;
  color: string;
  bg: string;
  sub?: string;
  trend?: number;
  progress?: number;
}> = ({ label, value, icon, color, bg, sub, trend, progress }) => (
  <div className="report-stat-card">
    <div className="report-stat-card__icon" style={{ background: bg, color }}>
      <IonIcon icon={icon} />
    </div>
    <div className="report-stat-card__body">
      <span className="report-stat-card__value">{value}</span>
      <span className="report-stat-card__label">{label}</span>
      {sub && <span className="report-stat-card__sub">{sub}</span>}
    </div>
    {trend != null && trend !== 0 && Math.abs(trend) <= 999 && (
      <span className={`report-stat-card__trend report-stat-card__trend--${trend > 0 ? 'up' : 'down'}`}>
        <IonIcon icon={trend > 0 ? arrowUp : arrowDown} />
        {' '}{Math.abs(trend)}%
      </span>
    )}
    {progress != null && (
      <div className="report-stat-card__progress">
        <div className="report-stat-card__progress-track">
          <div
            className="report-stat-card__progress-fill"
            style={{ width: `${Math.min(progress, 100)}%`, background: color }}
          />
        </div>
      </div>
    )}
  </div>
);

/* ── Tab type ── */
type ReportTab = 'overview' | 'members' | 'families' | 'groups' | 'gdpr' | 'registry';

/* ── RegistrySection: collapsible list for a category of removed members ── */
interface RegistrySectionProps {
  title: string;
  count: number;
  color: string;
  bgColor: string;
  icon: string;
  isOpen: boolean;
  onToggle: () => void;
  members: RegistryMember[];
  onMemberClick: (id: string) => void;
  renderMeta: (m: RegistryMember) => string;
}

const RegistrySection: React.FC<RegistrySectionProps> = React.memo(({
  title, count, color, bgColor, icon, isOpen, onToggle, members, onMemberClick, renderMeta,
}) => (
  <div className="reports-card reports-card--full" style={{ marginBottom: 12 }}>
    <button
      onClick={onToggle}
      style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        width: '100%', background: bgColor, border: 'none', borderRadius: 8,
        padding: '12px 16px', cursor: 'pointer',
      }}
    >
      <span style={{ fontWeight: 600, fontSize: 15, color }}>
        <IonIcon icon={icon} style={{ verticalAlign: -2, marginRight: 6 }} />
        {title} ({count})
      </span>
      <IonIcon icon={isOpen ? chevronUp : chevronDown} style={{ color, fontSize: 18 }} />
    </button>
    {isOpen ? (
      <div style={{ marginTop: 8 }}>
        <input
          className="form-input"
          placeholder="Caută persoană..."
          style={{ width: '100%', marginBottom: 8 }}
          onChange={(e) => {
            const el = e.target.nextElementSibling as HTMLElement;
            if (!el) return;
            const q = e.target.value.toLowerCase();
            Array.from(el.children).forEach((child) => {
              const name = child.textContent?.toLowerCase() ?? '';
              (child as HTMLElement).style.display = name.includes(q) ? '' : 'none';
            });
          }}
        />
        <div style={{ maxHeight: 400, overflowY: 'auto' }}>
          {members.map((m) => (
            <div
              key={m.id}
              onClick={() => onMemberClick(m.id)}
              style={{
                display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start',
                padding: '10px 12px', borderBottom: '1px solid #f0f0f3',
                cursor: 'pointer', fontSize: 13,
              }}
            >
              <div>
                <span style={{ color: '#1c1d21', fontWeight: 500 }}>{m.full_name}</span>
                <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 2 }}>{renderMeta(m)}</div>
              </div>
              {m.sector_name ? <span style={{ color: '#94a3b8', fontSize: 12, flexShrink: 0, marginLeft: 8 }}>{m.sector_name}</span> : null}
            </div>
          ))}
          {members.length === 0 ? (
            <p style={{ fontSize: 13, color: '#94a3b8', padding: '12px', textAlign: 'center' }}>
              Niciun membru în această categorie.
            </p>
          ) : null}
        </div>
      </div>
    ) : null}
  </div>
));

/* ═══════════════════════════════════════════ */
const ReportsPage: React.FC = () => {
  const router = useIonRouter();
  const { data: stats, isLoading } = useDashboardStats();
  const { data: trends } = useReportTrends();
  const { data: gdpr, isLoading: gdprLoading } = useGdprStats();
  const { data: registry, isLoading: registryLoading } = useRegistryStats();
  const [memberRanges, setMemberRanges] = useState<AgeRangeBucket[]>(
    () => loadCustomRanges(LS_KEY_CUSTOM_M, DEFAULT_MEMBER_RANGES),
  );
  const [dependentRanges, setDependentRanges] = useState<AgeRangeBucket[]>(
    () => loadCustomRanges(LS_KEY_CUSTOM_D, DEFAULT_DEPENDENT_RANGES),
  );

  const [debouncedMemberRanges, setDebouncedMemberRanges] = useState(memberRanges);
  const [debouncedDependentRanges, setDebouncedDependentRanges] = useState(dependentRanges);
  const debounceRef = useRef<ReturnType<typeof setTimeout>>(undefined);

  useEffect(() => {
    debounceRef.current = setTimeout(() => {
      setDebouncedMemberRanges(memberRanges);
      setDebouncedDependentRanges(dependentRanges);
    }, 600);
    return () => clearTimeout(debounceRef.current);
  }, [memberRanges, dependentRanges]);

  const ageParams = useMemo(() => ({
    age_ranges_members: JSON.stringify(debouncedMemberRanges),
    age_ranges_dependents: JSON.stringify(debouncedDependentRanges),
  }), [debouncedMemberRanges, debouncedDependentRanges]);

  const { data: breakdowns } = useMemberBreakdowns(ageParams);

  const handleMemberRangesChange = useCallback((ranges: AgeRangeBucket[]) => {
    setMemberRanges(ranges);
    localStorage.setItem(LS_KEY_CUSTOM_M, JSON.stringify(ranges));
  }, []);

  const handleDependentRangesChange = useCallback((ranges: AgeRangeBucket[]) => {
    setDependentRanges(ranges);
    localStorage.setItem(LS_KEY_CUSTOM_D, JSON.stringify(ranges));
  }, []);

  const [activeTab, setActiveTab] = useState<ReportTab>('overview');
  const [showUnsigned, setShowUnsigned] = useState(false);
  const [showSigned, setShowSigned] = useState(false);
  const [exportingGdpr, setExportingGdpr] = useState<'unsigned' | 'signed' | null>(null);
  const [openRegistrySections, setOpenRegistrySections] = useState<Set<string>>(() => new Set());

  const toggleRegistrySection = useCallback((key: string) => {
    setOpenRegistrySections((prev) => {
      const next = new Set(prev);
      if (next.has(key)) { next.delete(key); } else { next.add(key); }
      return next;
    });
  }, []);

  const handleGdprExport = useCallback(async (list: 'unsigned' | 'signed') => {
    if (exportingGdpr) return;
    setExportingGdpr(list);
    try {
      const blob = await exportGdprList(list);
      const suffix = list === 'signed' ? 'cu_gdpr' : 'fara_gdpr';
      downloadBlob(blob, `membri_${suffix}_${new Date().toISOString().slice(0, 10)}.xlsx`);
    } catch {
      // silently fail – network error
    } finally {
      setExportingGdpr(null);
    }
  }, [exportingGdpr]);

  const tabs: { key: ReportTab; label: string; icon: string }[] = [
    { key: 'overview', label: 'Sumar', icon: statsChart },
    { key: 'members', label: 'Persoane', icon: people },
    { key: 'families', label: 'Familii', icon: peopleCircle },
    { key: 'groups', label: 'Grupuri', icon: gitNetwork },
    { key: 'gdpr', label: 'GDPR', icon: shieldCheckmark },
    { key: 'registry', label: 'Registru', icon: documentTextOutline },
  ];

  /* ── Computed data ── */
  const memberTrend = useMemo(() => {
    if (!stats) return 0;
    const prev = stats.new_members_last_month || 1;
    return Math.round(((stats.new_members_this_month - prev) / prev) * 100);
  }, [stats]);

  /* Nivo pie data for gender */
  const genderPieData = useMemo(() => {
    if (!stats?.gender_distribution) return [];
    const labels: Record<string, string> = { male: 'Bărbați', female: 'Femei', M: 'Bărbați', F: 'Femei' };
    const colors: Record<string, string> = { male: '#5e81f4', female: '#f472b6', M: '#5e81f4', F: '#f472b6' };
    return Object.entries(stats.gender_distribution).map(([key, count]) => ({
      id: labels[key] ?? key,
      label: labels[key] ?? key,
      value: count,
      color: colors[key] ?? '#8181a5',
    }));
  }, [stats]);

  const statusData = useMemo(() => {
    if (!stats?.members_by_status) return [];
    const colors: Record<string, string> = {
      active: '#059669', under_discipline: '#d97706', transferred: '#64748b',
      left: '#94a3b8', excommunicated: '#dc2626', deceased: '#94a3b8',
    };
    return Object.entries(stats.members_by_status).map(([key, count]) => ({
      label: CHURCH_STATUS_LABELS[key as keyof typeof CHURCH_STATUS_LABELS] ?? key,
      count,
      pct: pct(count, stats.total_members),
      color: colors[key] ?? '#64748b',
    }));
  }, [stats]);

  /* Nivo bar data for sectors */
  const sectorBarData = useMemo(() => {
    if (!stats?.members_by_sector) return [];
    return stats.members_by_sector.map((s) => ({
      sector: s.sector_name,
      persoane: s.count,
    }));
  }, [stats]);

  const ageData = useMemo(() => {
    if (!stats?.age_distribution) return [];
    const max = Math.max(...stats.age_distribution.map((a) => a.count), 1);
    return stats.age_distribution.map((a) => ({
      ...a,
      pct: Math.round((a.count / max) * 100),
    }));
  }, [stats]);

  /* Shorten month labels: "mar. 2025" → "Mar 25" */
  const shortMonth = (m: string) => {
    const parts = m.replace('.', '').split(' ');
    const mon = parts[0] ? parts[0].charAt(0).toUpperCase() + parts[0].slice(1, 3) : m;
    const yr = parts[1] ? "'" + parts[1].slice(-2) : '';
    return `${mon} ${yr}`.trim();
  };

  /* Nivo line data for member trend */
  const memberTrendLine = useMemo(() => {
    if (!trends?.length) return [];
    return [{
      id: 'Total Persoane',
      color: '#5e81f4',
      data: trends.map((t) => ({ x: shortMonth(t.month), y: t.total_members })),
    }];
  }, [trends]);

  /* Nivo line data for visits */
  const visitsTrendLine = useMemo(() => {
    if (!trends?.length) return [];
    return [{
      id: 'Vizite',
      color: '#7ce7ac',
      data: trends.map((t) => ({ x: shortMonth(t.month), y: t.visits })),
    }];
  }, [trends]);

  /* Nivo bar data for new members per month */
  const newMembersBarData = useMemo(() => {
    if (!trends?.length) return [];
    return trends.map((t) => ({
      month: shortMonth(t.month),
      'Persoane Noi': t.new_members,
      'Vizite': t.visits,
    }));
  }, [trends]);

  if (isLoading) {
    return (
      <IonPage>
        <IonContent>
          <div className="form-loading"><IonSpinner name="crescent" /></div>
        </IonContent>
      </IonPage>
    );
  }

  return (
    <IonPage>
      <IonHeader>
        <IonToolbar className="crm-toolbar">
          <IonButtons slot="start">
            <IonMenuButton style={{ '--color': '#8181a5' } as React.CSSProperties} />
          </IonButtons>
          <IonTitle>Evidențe</IonTitle>
        </IonToolbar>
      </IonHeader>
      <IonContent style={{ '--background': '#f5f5fa' } as React.CSSProperties}>
        <div className="reports-page">

          {/* ── Tabs ── */}
          <div className="reports-tabs">
            {tabs.map((t) => (
              <button
                key={t.key}
                className={`reports-tab${activeTab === t.key ? ' reports-tab--active' : ''}`}
                onClick={() => setActiveTab(t.key)}
              >
                <IonIcon icon={t.icon} />
                <span>{t.label}</span>
              </button>
            ))}
          </div>

          {/* ── Overview Tab ── */}
          {activeTab === 'overview' && stats && (
            <>
              {/* Top stat cards with progress bars */}
              <div className="reports-stats-grid">
                <StatCard
                  label="Total Persoane" value={stats.total_members}
                  icon={people} color="#5e81f4" bg="#eef2ff"
                  sub={`${stats.active_members} activi (${pct(stats.active_members, stats.total_members)}%)`}
                  progress={pct(stats.active_members, stats.total_members)}
                />
                <StatCard
                  label="Total Familii" value={stats.total_families}
                  icon={home} color="#9698d6" bg="#f5f3ff"
                />
                <StatCard
                  label="Total Vizite" value={stats.total_visits ?? 0}
                  icon={eye} color="#7ce7ac" bg="#ecfdf5"
                />
                <StatCard
                  label="Persoane Noi" value={stats.new_members_this_month}
                  icon={people} color="#f4be5e" bg="#fffbeb"
                  trend={memberTrend}
                  sub={`Luna trecută: ${stats.new_members_last_month}`}
                />
              </div>

              {/* Charts row: Member trend line + Visits area */}
              <div className="reports-row">
                <div className="reports-card reports-card--chart">
                  <h3 className="reports-card__title">Evoluție Persoane</h3>
                  <div className="reports-chart-container">
                    {memberTrendLine.length > 0 ? (
                      <ResponsiveLine
                        data={memberTrendLine}
                        theme={nivoTheme}
                        margin={{ top: 16, right: 24, bottom: 40, left: 48 }}
                        xScale={{ type: 'point' }}
                        yScale={{ type: 'linear', min: 'auto', max: 'auto', stacked: false }}
                        curve="monotoneX"
                        colors={['#5e81f4']}
                        lineWidth={3}
                        enablePoints={true}
                        pointSize={8}
                        pointColor="#fff"
                        pointBorderWidth={2}
                        pointBorderColor="#5e81f4"
                        enableArea={true}
                        areaBaselineValue={memberTrendLine[0]?.data?.[0]?.y as number ?? 0}
                        areaOpacity={0.08}
                        enableGridX={false}
                        enableGridY={true}
                        axisBottom={{
                          tickSize: 0,
                          tickPadding: 12,
                          tickRotation: 0,
                        }}
                        axisLeft={{
                          tickSize: 0,
                          tickPadding: 12,
                          tickValues: 5,
                        }}
                        enableCrosshair={true}
                        useMesh={true}
                        animate={true}
                        motionConfig="gentle"
                      />
                    ) : (
                      <p className="reports-card__placeholder">Se încarcă datele...</p>
                    )}
                  </div>
                </div>

                <div className="reports-card reports-card--chart">
                  <h3 className="reports-card__title">Vizite Pastorale</h3>
                  <div className="reports-chart-container">
                    {visitsTrendLine.length > 0 ? (
                      <ResponsiveLine
                        data={visitsTrendLine}
                        theme={nivoTheme}
                        margin={{ top: 16, right: 24, bottom: 40, left: 48 }}
                        xScale={{ type: 'point' }}
                        yScale={{ type: 'linear', min: 0, max: 'auto', stacked: false }}
                        curve="monotoneX"
                        colors={['#7ce7ac']}
                        lineWidth={3}
                        enablePoints={true}
                        pointSize={8}
                        pointColor="#fff"
                        pointBorderWidth={2}
                        pointBorderColor="#7ce7ac"
                        enableArea={true}
                        areaOpacity={0.12}
                        enableGridX={false}
                        enableGridY={true}
                        axisBottom={{
                          tickSize: 0,
                          tickPadding: 12,
                          tickRotation: 0,
                        }}
                        axisLeft={{
                          tickSize: 0,
                          tickPadding: 12,
                          tickValues: 5,
                        }}
                        enableCrosshair={true}
                        useMesh={true}
                        animate={true}
                        motionConfig="gentle"
                      />
                    ) : (
                      <p className="reports-card__placeholder">Se încarcă datele...</p>
                    )}
                  </div>
                </div>
              </div>

              {/* Charts row: Sector bars + Gender donut + Status bars */}
              <div className="reports-row reports-row--triple">
                {/* Sector vertical bars */}
                <div className="reports-card reports-card--chart">
                  <h3 className="reports-card__title">Persoane pe Sectoare</h3>
                  <div className="reports-chart-container">
                    {sectorBarData.length > 0 ? (
                      <ResponsiveBar
                        data={sectorBarData}
                        keys={['persoane']}
                        indexBy="sector"
                        theme={nivoTheme}
                        margin={{ top: 8, right: 16, bottom: 48, left: 48 }}
                        padding={0.35}
                        colors={['#5e81f4']}
                        borderRadius={6}
                        enableLabel={true}
                        label={(d) => `${d.value}`}
                        labelTextColor="#fff"
                        axisBottom={{
                          tickSize: 0,
                          tickPadding: 8,
                          tickRotation: 0,
                        }}
                        axisLeft={{
                          tickSize: 0,
                          tickPadding: 8,
                          tickValues: 5,
                        }}
                        enableGridY={true}
                        animate={true}
                        motionConfig="gentle"
                      />
                    ) : (
                      <p className="reports-card__placeholder">Nu sunt sectoare definite.</p>
                    )}
                  </div>
                </div>

                {/* Gender donut */}
                <div className="reports-card reports-card--chart">
                  <h3 className="reports-card__title">Distribuție Gen</h3>
                  <div className="reports-chart-container reports-chart-container--pie">
                    {genderPieData.length > 0 ? (
                      <ResponsivePie
                        data={genderPieData}
                        theme={nivoTheme}
                        margin={{ top: 16, right: 16, bottom: 16, left: 16 }}
                        innerRadius={0.6}
                        padAngle={2}
                        cornerRadius={4}
                        activeOuterRadiusOffset={6}
                        colors={{ datum: 'data.color' }}
                        borderWidth={0}
                        enableArcLinkLabels={true}
                        arcLinkLabelsSkipAngle={10}
                        arcLinkLabelsTextColor="#1c1d21"
                        arcLinkLabelsColor={{ from: 'color' }}
                        arcLinkLabelsThickness={2}
                        arcLabelsSkipAngle={10}
                        arcLabelsTextColor="#fff"
                        animate={true}
                        motionConfig="gentle"
                      />
                    ) : (
                      <p className="reports-card__placeholder">Nu sunt date de gen.</p>
                    )}
                    {genderPieData.length > 0 && (
                      <div className="reports-pie-center">
                        <span className="reports-pie-center__value">{stats.total_members}</span>
                        <span className="reports-pie-center__label">total</span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Status distribution bars */}
                <div className="reports-card">
                  <h3 className="reports-card__title">Distribuție Status</h3>
                  <div className="reports-bars">
                    {statusData.map((s) => (
                      <div key={s.label} className="reports-bar-row">
                        <div className="reports-bar-row__label">
                          <span className="reports-bar-row__dot" style={{ background: s.color }} />
                          {s.label}
                        </div>
                        <div className="reports-bar-row__track">
                          <div className="reports-bar-row__fill" style={{ width: `${s.pct}%`, background: s.color }} />
                        </div>
                        <span className="reports-bar-row__count">{s.count}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Bottom row: New members + visits combined bar chart + Age distribution */}
              <div className="reports-row">
                <div className="reports-card reports-card--chart">
                  <h3 className="reports-card__title">Activitate Lunară</h3>
                  <div className="reports-chart-container">
                    {newMembersBarData.length > 0 ? (
                      <ResponsiveBar
                        data={newMembersBarData}
                        keys={['Persoane Noi', 'Vizite']}
                        indexBy="month"
                        theme={nivoTheme}
                        margin={{ top: 8, right: 16, bottom: 48, left: 48 }}
                        padding={0.3}
                        groupMode="grouped"
                        colors={['#5e81f4', '#7ce7ac']}
                        borderRadius={4}
                        enableLabel={false}
                        axisBottom={{
                          tickSize: 0,
                          tickPadding: 8,
                          tickRotation: 0,
                        }}
                        axisLeft={{
                          tickSize: 0,
                          tickPadding: 8,
                          tickValues: 5,
                        }}
                        enableGridY={true}
                        animate={true}
                        motionConfig="gentle"
                        legends={[
                          {
                            dataFrom: 'keys',
                            anchor: 'top-right',
                            direction: 'row',
                            translateY: -4,
                            itemWidth: 100,
                            itemHeight: 20,
                            itemTextColor: '#8181a5',
                            symbolSize: 10,
                            symbolShape: 'circle',
                          },
                        ]}
                      />
                    ) : (
                      <p className="reports-card__placeholder">Se încarcă datele...</p>
                    )}
                  </div>
                </div>

                <div className="reports-card">
                  <h3 className="reports-card__title">Distribuție Vârstă</h3>
                  <div className="reports-bars">
                    {ageData.map((a) => (
                      <div key={a.range} className="reports-bar-row">
                        <div className="reports-bar-row__label">{a.range}</div>
                        <div className="reports-bar-row__track">
                          <div className="reports-bar-row__fill" style={{ width: `${a.pct}%`, background: '#9698d6' }} />
                        </div>
                        <span className="reports-bar-row__count">{a.count}</span>
                      </div>
                    ))}
                    {ageData.length === 0 && (
                      <p style={{ fontSize: 13, color: '#94a3b8', margin: 0 }}>Nu sunt date de vârstă disponibile.</p>
                    )}
                  </div>
                </div>
              </div>
            </>
          )}

          {/* ── Members Tab ── */}
          {activeTab === 'members' && stats && (
            <>
              <div className="reports-row">
                <div className="reports-card reports-card--full">
                  <h3 className="reports-card__title">Statistici Persoane</h3>
                  <div className="reports-kpi-grid">
                    <div className="reports-kpi">
                      <span className="reports-kpi__value">{stats.total_members}</span>
                      <span className="reports-kpi__label">Total</span>
                    </div>
                    <div className="reports-kpi">
                      <span className="reports-kpi__value" style={{ color: '#059669' }}>{stats.active_members}</span>
                      <span className="reports-kpi__label">Activi</span>
                    </div>
                    <div className="reports-kpi">
                      <span className="reports-kpi__value" style={{ color: '#dc2626' }}>{stats.inactive_members}</span>
                      <span className="reports-kpi__label">Inactivi</span>
                    </div>
                    <div className="reports-kpi">
                      <span className="reports-kpi__value" style={{ color: '#d97706' }}>{stats.new_members_this_month}</span>
                      <span className="reports-kpi__label">Noi luna aceasta</span>
                    </div>
                  </div>
                </div>
              </div>

              <div className="reports-row">
                <div className="reports-card">
                  <h3 className="reports-card__title">Per Tip Membru</h3>
                  <div className="reports-bars">
                    {Object.entries(stats.members_by_type).map(([key, count]) => (
                      <div key={key} className="reports-bar-row">
                        <div className="reports-bar-row__label">
                          {MEMBERSHIP_TYPE_LABELS[key as keyof typeof MEMBERSHIP_TYPE_LABELS] ?? key}
                        </div>
                        <div className="reports-bar-row__track">
                          <div className="reports-bar-row__fill" style={{ width: `${pct(count, stats.total_members)}%`, background: key === 'member' ? '#5e81f4' : '#9698d6' }} />
                        </div>
                        <span className="reports-bar-row__count">{count}</span>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="reports-card">
                  <h3 className="reports-card__title">Per Status</h3>
                  <div className="reports-bars">
                    {statusData.map((s) => (
                      <div key={s.label} className="reports-bar-row">
                        <div className="reports-bar-row__label">
                          <span className="reports-bar-row__dot" style={{ background: s.color }} />
                          {s.label}
                        </div>
                        <div className="reports-bar-row__track">
                          <div className="reports-bar-row__fill" style={{ width: `${s.pct}%`, background: s.color }} />
                        </div>
                        <span className="reports-bar-row__count">{s.count} ({pct(s.count, stats.total_members)}%)</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* ── Gender by membership type (2 donuts) ── */}
              {breakdowns ? (
                <>
                  <div className="reports-row">
                    <div className="reports-card reports-card--chart">
                      <h3 className="reports-card__title">Gen — Membri</h3>
                      <div className="reports-chart-container reports-chart-container--pie">
                        {Object.keys(breakdowns.gender_members).length > 0 ? (
                          <ResponsivePie
                            data={Object.entries(breakdowns.gender_members).map(([key, count]) => ({
                              id: key === 'M' ? 'Bărbați' : 'Femei',
                              label: key === 'M' ? 'Bărbați' : 'Femei',
                              value: count,
                              color: key === 'M' ? '#5e81f4' : '#f472b6',
                            }))}
                            theme={nivoTheme}
                            margin={{ top: 16, right: 16, bottom: 16, left: 16 }}
                            innerRadius={0.6}
                            padAngle={2}
                            cornerRadius={4}
                            activeOuterRadiusOffset={6}
                            colors={{ datum: 'data.color' }}
                            borderWidth={0}
                            enableArcLinkLabels={true}
                            arcLinkLabelsSkipAngle={10}
                            arcLinkLabelsTextColor="#1c1d21"
                            arcLinkLabelsColor={{ from: 'color' }}
                            arcLinkLabelsThickness={2}
                            arcLabelsSkipAngle={10}
                            arcLabelsTextColor="#fff"
                            animate={true}
                            motionConfig="gentle"
                          />
                        ) : (
                          <p className="reports-card__placeholder">Nu sunt date.</p>
                        )}
                        {Object.keys(breakdowns.gender_members).length > 0 ? (
                          <div className="reports-pie-center">
                            <span className="reports-pie-center__value">{Object.values(breakdowns.gender_members).reduce((a, b) => a + b, 0)}</span>
                            <span className="reports-pie-center__label">membri</span>
                          </div>
                        ) : null}
                      </div>
                    </div>

                    <div className="reports-card reports-card--chart">
                      <h3 className="reports-card__title">Gen — Aparținători</h3>
                      <div className="reports-chart-container reports-chart-container--pie">
                        {Object.keys(breakdowns.gender_dependents).length > 0 ? (
                          <ResponsivePie
                            data={Object.entries(breakdowns.gender_dependents).map(([key, count]) => ({
                              id: key === 'M' ? 'Băieți' : 'Fete',
                              label: key === 'M' ? 'Băieți' : 'Fete',
                              value: count,
                              color: key === 'M' ? '#5e81f4' : '#f472b6',
                            }))}
                            theme={nivoTheme}
                            margin={{ top: 16, right: 16, bottom: 16, left: 16 }}
                            innerRadius={0.6}
                            padAngle={2}
                            cornerRadius={4}
                            activeOuterRadiusOffset={6}
                            colors={{ datum: 'data.color' }}
                            borderWidth={0}
                            enableArcLinkLabels={true}
                            arcLinkLabelsSkipAngle={10}
                            arcLinkLabelsTextColor="#1c1d21"
                            arcLinkLabelsColor={{ from: 'color' }}
                            arcLinkLabelsThickness={2}
                            arcLabelsSkipAngle={10}
                            arcLabelsTextColor="#fff"
                            animate={true}
                            motionConfig="gentle"
                          />
                        ) : (
                          <p className="reports-card__placeholder">Nu sunt date.</p>
                        )}
                        {Object.keys(breakdowns.gender_dependents).length > 0 ? (
                          <div className="reports-pie-center">
                            <span className="reports-pie-center__value">{Object.values(breakdowns.gender_dependents).reduce((a, b) => a + b, 0)}</span>
                            <span className="reports-pie-center__label">aparțin.</span>
                          </div>
                        ) : null}
                      </div>
                    </div>
                  </div>

                  {/* ── Age distributions (2 bar charts) ── */}
                  <div className="age-editors-row">
                    <AgeRangeEditor label="Membri" ranges={memberRanges} onChange={handleMemberRangesChange} />
                    <AgeRangeEditor label="Aparținători" ranges={dependentRanges} onChange={handleDependentRangesChange} />
                  </div>
                  <div className="reports-row">
                    <div className="reports-card reports-card--chart">
                      <h3 className="reports-card__title">Vârstă — Membri</h3>
                      <div className="reports-chart-container">
                        {breakdowns.age_members.some(a => a.count > 0) ? (
                          <ResponsiveBar
                            data={breakdowns.age_members.map(a => ({ range: a.range, persoane: a.count }))}
                            keys={['persoane']}
                            indexBy="range"
                            theme={nivoTheme}
                            margin={{ top: 8, right: 16, bottom: 48, left: 48 }}
                            padding={0.35}
                            colors={['#5e81f4']}
                            borderRadius={6}
                            enableLabel={true}
                            label={(d) => `${d.value}`}
                            labelTextColor="#fff"
                            axisBottom={{ tickSize: 0, tickPadding: 8, tickRotation: 0 }}
                            axisLeft={{ tickSize: 0, tickPadding: 8, tickValues: 5 }}
                            enableGridY={true}
                            animate={true}
                            motionConfig="gentle"
                          />
                        ) : (
                          <p className="reports-card__placeholder">Nu sunt date de vârstă.</p>
                        )}
                      </div>
                    </div>

                    <div className="reports-card reports-card--chart">
                      <h3 className="reports-card__title">Vârstă — Aparținători</h3>
                      <div className="reports-chart-container">
                        {breakdowns.age_dependents.some(a => a.count > 0) ? (
                          <ResponsiveBar
                            data={breakdowns.age_dependents.map(a => ({ range: a.range, persoane: a.count }))}
                            keys={['persoane']}
                            indexBy="range"
                            theme={nivoTheme}
                            margin={{ top: 8, right: 16, bottom: 48, left: 48 }}
                            padding={0.35}
                            colors={['#9698d6']}
                            borderRadius={6}
                            enableLabel={true}
                            label={(d) => `${d.value}`}
                            labelTextColor="#fff"
                            axisBottom={{ tickSize: 0, tickPadding: 8, tickRotation: 0 }}
                            axisLeft={{ tickSize: 0, tickPadding: 8, tickValues: 5 }}
                            enableGridY={true}
                            animate={true}
                            motionConfig="gentle"
                          />
                        ) : (
                          <p className="reports-card__placeholder">Nu sunt date de vârstă.</p>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* ── Civil status (members only) ── */}
                  <div className="reports-row">
                    <div className="reports-card reports-card--chart">
                      <h3 className="reports-card__title">Stare Civilă — Membri</h3>
                      <div className="reports-chart-container reports-chart-container--pie">
                        {Object.keys(breakdowns.civil_status).length > 0 ? (
                          <ResponsivePie
                            data={(() => {
                              const colors: Record<string, string> = {
                                married: '#059669', unmarried: '#5e81f4', widowed: '#8b5cf6',
                                divorced: '#d97706', separated: '#dc2626', unspecified: '#94a3b8',
                              };
                              return Object.entries(breakdowns.civil_status).map(([key, count]) => ({
                                id: CIVIL_STATUS_LABELS_ALL[key] ?? key,
                                label: CIVIL_STATUS_LABELS_ALL[key] ?? key,
                                value: count,
                                color: colors[key] ?? '#64748b',
                              }));
                            })()}
                            theme={nivoTheme}
                            margin={{ top: 16, right: 16, bottom: 16, left: 16 }}
                            innerRadius={0.6}
                            padAngle={2}
                            cornerRadius={4}
                            activeOuterRadiusOffset={6}
                            colors={{ datum: 'data.color' }}
                            borderWidth={0}
                            enableArcLinkLabels={true}
                            arcLinkLabelsSkipAngle={10}
                            arcLinkLabelsTextColor="#1c1d21"
                            arcLinkLabelsColor={{ from: 'color' }}
                            arcLinkLabelsThickness={2}
                            arcLabelsSkipAngle={10}
                            arcLabelsTextColor="#fff"
                            animate={true}
                            motionConfig="gentle"
                          />
                        ) : (
                          <p className="reports-card__placeholder">Nu sunt date de stare civilă.</p>
                        )}
                        {Object.keys(breakdowns.civil_status).length > 0 ? (
                          <div className="reports-pie-center">
                            <span className="reports-pie-center__value">{Object.values(breakdowns.civil_status).reduce((a, b) => a + b, 0)}</span>
                            <span className="reports-pie-center__label">membri</span>
                          </div>
                        ) : null}
                      </div>
                    </div>

                    <div className="reports-card">
                      <h3 className="reports-card__title">Detalii Stare Civilă</h3>
                      <div className="reports-bars">
                        {(() => {
                          const colors: Record<string, string> = {
                            married: '#059669', unmarried: '#5e81f4', widowed: '#8b5cf6',
                            divorced: '#d97706', separated: '#dc2626', unspecified: '#94a3b8',
                          };
                          const total = Object.values(breakdowns.civil_status).reduce((a, b) => a + b, 0);
                          return Object.entries(breakdowns.civil_status).map(([key, count]) => (
                            <div key={key} className="reports-bar-row">
                              <div className="reports-bar-row__label">
                                <span className="reports-bar-row__dot" style={{ background: colors[key] ?? '#64748b' }} />
                                {CIVIL_STATUS_LABELS_ALL[key] ?? key}
                              </div>
                              <div className="reports-bar-row__track">
                                <div className="reports-bar-row__fill" style={{ width: `${pct(count, total)}%`, background: colors[key] ?? '#64748b' }} />
                              </div>
                              <span className="reports-bar-row__count">{count} ({pct(count, total)}%)</span>
                            </div>
                          ));
                        })()}
                      </div>
                    </div>
                  </div>

                  {/* ── Baptism origin (members) — where they were water-baptized ── */}
                  <div className="reports-row">
                    <div className="reports-card">
                      <h3 className="reports-card__title">Botez apă — Locul</h3>
                      <p style={{ fontSize: 12, color: '#94a3b8', margin: '0 0 10px' }}>
                        Estimare după textul „locul botezului”. Cuvânt-cheie „acasă”:{' '}
                        <strong>{breakdowns.baptism_origin.home_keyword || '—'}</strong>
                      </p>
                      <div className="reports-bars">
                        {(() => {
                          const o = breakdowns.baptism_origin;
                          const total = o.here + o.elsewhere + o.unspecified;
                          const items = [
                            { key: 'here', label: 'La biserica noastră', value: o.here, color: '#059669' },
                            { key: 'elsewhere', label: 'În altă parte', value: o.elsewhere, color: '#d97706' },
                            { key: 'unspecified', label: 'Nespecificat', value: o.unspecified, color: '#94a3b8' },
                          ];
                          return items.map((it) => (
                            <div key={it.key} className="reports-bar-row">
                              <div className="reports-bar-row__label">
                                <span className="reports-bar-row__dot" style={{ background: it.color }} />
                                {it.label}
                              </div>
                              <div className="reports-bar-row__track">
                                <div className="reports-bar-row__fill" style={{ width: `${pct(it.value, total)}%`, background: it.color }} />
                              </div>
                              <span className="reports-bar-row__count">{it.value} ({pct(it.value, total)}%)</span>
                            </div>
                          ));
                        })()}
                      </div>
                    </div>

                    <div className="reports-card">
                      <h3 className="reports-card__title">Locuri de botez (top)</h3>
                      <div className="reports-bars">
                        {breakdowns.baptism_places.length > 0 ? (
                          (() => {
                            const max = Math.max(...breakdowns.baptism_places.map((p) => p.count), 1);
                            return breakdowns.baptism_places.map((p) => (
                              <div key={p.place} className="reports-bar-row">
                                <div className="reports-bar-row__label">{p.place}</div>
                                <div className="reports-bar-row__track">
                                  <div className="reports-bar-row__fill" style={{ width: `${Math.round((p.count / max) * 100)}%`, background: '#5e81f4' }} />
                                </div>
                                <span className="reports-bar-row__count">{p.count}</span>
                              </div>
                            ));
                          })()
                        ) : (
                          <p className="reports-card__placeholder">Nu sunt date despre locul botezului.</p>
                        )}
                      </div>
                    </div>
                  </div>
                </>
              ) : null}
            </>
          )}

          {/* ── Families Tab (placeholder) ── */}
          {activeTab === 'families' && stats && (
            <div className="reports-row">
              <div className="reports-card reports-card--full">
                <h3 className="reports-card__title">Statistici Familii</h3>
                <div className="reports-kpi-grid">
                  <div className="reports-kpi">
                    <span className="reports-kpi__value">{stats.total_families}</span>
                    <span className="reports-kpi__label">Total Familii</span>
                  </div>
                </div>
                <p className="reports-card__placeholder">Rapoartele detaliate pentru familii vor fi disponibile în curând.</p>
              </div>
            </div>
          )}

          {/* ── Groups Tab (placeholder) ── */}
          {activeTab === 'groups' && stats && (
            <div className="reports-row">
              <div className="reports-card reports-card--full">
                <h3 className="reports-card__title">Statistici Grupuri</h3>
                <div className="reports-kpi-grid">
                  <div className="reports-kpi">
                    <span className="reports-kpi__value">{stats.total_groups}</span>
                    <span className="reports-kpi__label">Total Grupuri</span>
                  </div>
                </div>
                <p className="reports-card__placeholder">Rapoartele detaliate pentru grupuri vor fi disponibile în curând.</p>
              </div>
            </div>
          )}

          {/* ── GDPR Tab ── */}
          {activeTab === 'gdpr' && (
            <>
              {gdprLoading && (
                <div className="form-loading"><IonSpinner name="crescent" /></div>
              )}
              {!gdprLoading && !gdpr && (
                <div className="reports-row">
                  <div className="reports-card reports-card--full">
                    <p style={{ fontSize: 14, color: '#dc2626', margin: 0 }}>
                      Nu s-au putut încărca datele GDPR. Verifică dacă serverul este actualizat.
                    </p>
                  </div>
                </div>
              )}
              {!gdprLoading && gdpr && (
                <>
                  {/* KPI cards */}
                  <div className="reports-stats-grid">
                    <StatCard
                      label="Total Persoane" value={gdpr.total_members}
                      icon={people} color="#5e81f4" bg="#eef2ff"
                    />
                    <StatCard
                      label="GDPR Semnat" value={gdpr.signed}
                      icon={checkmarkCircle} color="#059669" bg="#ecfdf5"
                      sub={`${gdpr.signed_pct}%`}
                      progress={gdpr.signed_pct}
                    />
                    <StatCard
                      label="GDPR Nesemnat" value={gdpr.unsigned}
                      icon={closeCircle} color="#dc2626" bg="#fef2f2"
                      sub={`${gdpr.total_members > 0 ? Math.round(100 - gdpr.signed_pct) : 0}%`}
                      progress={gdpr.total_members > 0 ? 100 - gdpr.signed_pct : 0}
                    />
                  </div>

                  {/* Donut chart */}
                  <div className="reports-row">
                    <div className="reports-card reports-card--chart">
                      <h3 className="reports-card__title">Distribuție GDPR</h3>
                      <div className="reports-chart-container reports-chart-container--pie">
                        {(gdpr.signed > 0 || gdpr.unsigned > 0) ? (
                          <ResponsivePie
                            data={[
                              ...(gdpr.signed > 0 ? [{ id: 'Semnat', label: 'Semnat', value: gdpr.signed, color: '#059669' }] : []),
                              ...(gdpr.unsigned > 0 ? [{ id: 'Nesemnat', label: 'Nesemnat', value: gdpr.unsigned, color: '#dc2626' }] : []),
                            ]}
                            theme={nivoTheme}
                            margin={{ top: 16, right: 16, bottom: 16, left: 16 }}
                            innerRadius={0.6}
                            padAngle={2}
                            cornerRadius={4}
                            activeOuterRadiusOffset={6}
                            colors={{ datum: 'data.color' }}
                            borderWidth={0}
                            enableArcLinkLabels={true}
                            arcLinkLabelsSkipAngle={10}
                            arcLinkLabelsTextColor="#1c1d21"
                            arcLinkLabelsColor={{ from: 'color' }}
                            arcLinkLabelsThickness={2}
                            arcLabelsSkipAngle={10}
                            arcLabelsTextColor="#fff"
                            animate={true}
                            motionConfig="gentle"
                          />
                        ) : (
                          <p className="reports-card__placeholder">Nu sunt date disponibile.</p>
                        )}
                        <div className="reports-pie-center">
                          <span className="reports-pie-center__value">{gdpr.total_members}</span>
                          <span className="reports-pie-center__label">total</span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Unsigned members list */}
                  <div className="reports-row">
                    <div className="reports-card reports-card--full">
                      <div style={{
                        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                        background: '#fef2f2', borderRadius: 8, padding: '12px 16px',
                      }}>
                        <button
                          onClick={() => setShowUnsigned(!showUnsigned)}
                          style={{
                            display: 'flex', alignItems: 'center', gap: 8, flex: 1,
                            background: 'none', border: 'none', cursor: 'pointer', padding: 0,
                          }}
                        >
                          <span style={{ fontWeight: 600, fontSize: 15, color: '#dc2626' }}>
                            <IonIcon icon={closeCircle} style={{ verticalAlign: -2, marginRight: 6 }} />
                            Fără GDPR ({gdpr.unsigned})
                          </span>
                          <IonIcon icon={showUnsigned ? chevronUp : chevronDown} style={{ color: '#dc2626', fontSize: 18 }} />
                        </button>
                        {gdpr.unsigned > 0 && (
                          <button
                            onClick={() => handleGdprExport('unsigned')}
                            disabled={exportingGdpr === 'unsigned'}
                            style={{
                              display: 'flex', alignItems: 'center', gap: 4,
                              background: '#dc2626', color: '#fff', border: 'none', borderRadius: 6,
                              padding: '6px 12px', cursor: 'pointer', fontSize: 12, fontWeight: 600,
                              opacity: exportingGdpr === 'unsigned' ? 0.6 : 1,
                            }}
                          >
                            {exportingGdpr === 'unsigned'
                              ? <IonSpinner name="crescent" style={{ width: 14, height: 14, color: '#fff' }} />
                              : <IonIcon icon={downloadOutline} style={{ fontSize: 14 }} />}
                            Excel
                          </button>
                        )}
                      </div>
                      {showUnsigned && (
                        <div style={{ marginTop: 8 }}>
                          <input
                            className="form-input"
                            placeholder="Caută persoană..."
                            style={{ width: '100%', marginBottom: 8 }}
                            onChange={(e) => {
                              const el = e.target.nextElementSibling as HTMLElement;
                              if (!el) return;
                              const q = e.target.value.toLowerCase();
                              Array.from(el.children).forEach((child) => {
                                const name = child.textContent?.toLowerCase() ?? '';
                                (child as HTMLElement).style.display = name.includes(q) ? '' : 'none';
                              });
                            }}
                          />
                          <div style={{ maxHeight: 400, overflowY: 'auto' }}>
                            {gdpr.unsigned_members.map((m) => (
                              <div
                                key={m.id}
                                onClick={() => router.push(`/members/${m.id}`)}
                                style={{
                                  display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                                  padding: '10px 12px', borderBottom: '1px solid #f0f0f3',
                                  cursor: 'pointer', fontSize: 13,
                                }}
                              >
                                <span style={{ color: '#1c1d21', fontWeight: 500 }}>{m.full_name}</span>
                                {m.sector_name && <span style={{ color: '#94a3b8', fontSize: 12 }}>{m.sector_name}</span>}
                              </div>
                            ))}
                            {gdpr.unsigned_members.length === 0 && (
                              <p style={{ fontSize: 13, color: '#059669', padding: '12px', textAlign: 'center' }}>Toți au semnat GDPR!</p>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Signed members list */}
                  <div className="reports-row">
                    <div className="reports-card reports-card--full">
                      <div style={{
                        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                        background: '#ecfdf5', borderRadius: 8, padding: '12px 16px',
                      }}>
                        <button
                          onClick={() => setShowSigned(!showSigned)}
                          style={{
                            display: 'flex', alignItems: 'center', gap: 8, flex: 1,
                            background: 'none', border: 'none', cursor: 'pointer', padding: 0,
                          }}
                        >
                          <span style={{ fontWeight: 600, fontSize: 15, color: '#059669' }}>
                            <IonIcon icon={checkmarkCircle} style={{ verticalAlign: -2, marginRight: 6 }} />
                            Cu GDPR ({gdpr.signed})
                          </span>
                          <IonIcon icon={showSigned ? chevronUp : chevronDown} style={{ color: '#059669', fontSize: 18 }} />
                        </button>
                        {gdpr.signed > 0 && (
                          <button
                            onClick={() => handleGdprExport('signed')}
                            disabled={exportingGdpr === 'signed'}
                            style={{
                              display: 'flex', alignItems: 'center', gap: 4,
                              background: '#059669', color: '#fff', border: 'none', borderRadius: 6,
                              padding: '6px 12px', cursor: 'pointer', fontSize: 12, fontWeight: 600,
                              opacity: exportingGdpr === 'signed' ? 0.6 : 1,
                            }}
                          >
                            {exportingGdpr === 'signed'
                              ? <IonSpinner name="crescent" style={{ width: 14, height: 14, color: '#fff' }} />
                              : <IonIcon icon={downloadOutline} style={{ fontSize: 14 }} />}
                            Excel
                          </button>
                        )}
                      </div>
                      {showSigned && (
                        <div style={{ marginTop: 8 }}>
                          <input
                            className="form-input"
                            placeholder="Caută persoană..."
                            style={{ width: '100%', marginBottom: 8 }}
                            onChange={(e) => {
                              const el = e.target.nextElementSibling as HTMLElement;
                              if (!el) return;
                              const q = e.target.value.toLowerCase();
                              Array.from(el.children).forEach((child) => {
                                const name = child.textContent?.toLowerCase() ?? '';
                                (child as HTMLElement).style.display = name.includes(q) ? '' : 'none';
                              });
                            }}
                          />
                          <div style={{ maxHeight: 400, overflowY: 'auto' }}>
                            {gdpr.signed_members.map((m) => (
                              <div
                                key={m.id}
                                onClick={() => router.push(`/members/${m.id}`)}
                                style={{
                                  display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                                  padding: '10px 12px', borderBottom: '1px solid #f0f0f3',
                                  cursor: 'pointer', fontSize: 13,
                                }}
                              >
                                <span style={{ color: '#1c1d21', fontWeight: 500 }}>{m.full_name}</span>
                                {m.sector_name && <span style={{ color: '#94a3b8', fontSize: 12 }}>{m.sector_name}</span>}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </>
              )}
            </>
          )}

          {/* ── Registry Tab ── */}
          {activeTab === 'registry' && (
            <>
              {registryLoading ? (
                <div className="form-loading"><IonSpinner name="crescent" /></div>
              ) : null}
              {!registryLoading && !registry ? (
                <div className="reports-row">
                  <div className="reports-card reports-card--full">
                    <p style={{ fontSize: 14, color: '#dc2626', margin: 0 }}>
                      Nu s-au putut încărca datele registrului.
                    </p>
                  </div>
                </div>
              ) : null}
              {!registryLoading && registry ? (
                <>
                  <div className="reports-stats-grid">
                    <StatCard
                      label="Transferați" value={registry.transferred.count}
                      icon={swapHorizontalOutline} color="#0891b2" bg="#ecfeff"
                    />
                    <StatCard
                      label="Decedați" value={registry.deceased.count}
                      icon={skullOutline} color="#64748b" bg="#f8fafc"
                    />
                    <StatCard
                      label="Nebotezați / Risipitori" value={registry.prodigal.count}
                      icon={walkOutline} color="#d97706" bg="#fffbeb"
                    />
                  </div>

                  <div className="reports-row">
                    <div className="reports-card reports-card--full" style={{ padding: 0, background: 'transparent', boxShadow: 'none' }}>
                      <RegistrySection
                        title="Decedați"
                        count={registry.deceased.count}
                        color="#64748b"
                        bgColor="#f8fafc"
                        icon={skullOutline}
                        isOpen={openRegistrySections.has('deceased')}
                        onToggle={() => toggleRegistrySection('deceased')}
                        members={registry.deceased.members}
                        onMemberClick={(id) => router.push(`/members/${id}`)}
                        renderMeta={(m) => [
                          m.death_date ? `Decedat: ${new Date(m.death_date).toLocaleDateString('ro-RO')}` : null,
                          m.status_date ? `Decizie: ${new Date(m.status_date).toLocaleDateString('ro-RO')}` : null,
                          m.reason,
                        ].filter(Boolean).join(' · ')}
                      />
                      <RegistrySection
                        title="Transferați"
                        count={registry.transferred.count}
                        color="#0891b2"
                        bgColor="#ecfeff"
                        icon={swapHorizontalOutline}
                        isOpen={openRegistrySections.has('transferred')}
                        onToggle={() => toggleRegistrySection('transferred')}
                        members={registry.transferred.members}
                        onMemberClick={(id) => router.push(`/members/${id}`)}
                        renderMeta={(m) => [
                          m.origin_status && m.origin_status !== 'transferred'
                            ? (CHURCH_STATUS_LABELS[m.origin_status as keyof typeof CHURCH_STATUS_LABELS] ?? m.origin_status)
                            : null,
                          m.transfer_church ? `Biserica: ${m.transfer_church}` : null,
                          m.transfer_city ? `Oraș: ${m.transfer_city}` : null,
                          m.status_date ? `Data: ${new Date(m.status_date).toLocaleDateString('ro-RO')}` : null,
                        ].filter(Boolean).join(' · ')}
                      />
                      <RegistrySection
                        title="Nebotezați / Fii Risipitori"
                        count={registry.prodigal.count}
                        color="#d97706"
                        bgColor="#fffbeb"
                        icon={walkOutline}
                        isOpen={openRegistrySections.has('prodigal')}
                        onToggle={() => toggleRegistrySection('prodigal')}
                        members={registry.prodigal.members}
                        onMemberClick={(id) => router.push(`/members/${id}`)}
                        renderMeta={(m) => m.church_status ? `Status: ${m.church_status}` : ''}
                      />
                    </div>
                  </div>
                </>
              ) : null}
            </>
          )}

        </div>
      </IonContent>
    </IonPage>
  );
};

export default ReportsPage;
